import { zodResolver } from '@hookform/resolvers/zod';
import * as React from 'react';
import { useForm } from 'react-hook-form';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FormField,
  Input,
} from '@payment-plan/ui';
import { projectSchema, type ProjectFormValues } from './project.schema';
import { AYUDA_CODIGO, registroEnMayusculas } from '../codigo-consecutivo';
import { useCreateProject, useUpdateProject } from './use-projects';
import type { Project } from '../../../types/admin';

interface ProjectFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  project?: Project | null;
  mode?: 'view' | 'edit';
}

const EMPTY: ProjectFormValues = {
  codigo: '',
  nombre: '',
  direccion: '',
  ciudad: '',
  matriculaMatriz: '',
};

export function ProjectFormDialog({
  open,
  onOpenChange,
  project,
  mode = 'edit',
}: ProjectFormDialogProps): React.JSX.Element {
  const isView = mode === 'view';
  const isEdit = Boolean(project);
  const createProject = useCreateProject();
  const updateProject = useUpdateProject();
  const [serverError, setServerError] = React.useState<string | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<ProjectFormValues>({
    resolver: zodResolver(projectSchema),
    defaultValues: EMPTY,
  });

  React.useEffect(() => {
    if (!open) {
      return;
    }
    setServerError(null);
    reset(
      project
        ? {
            codigo: project.codigo,
            nombre: project.nombre,
            direccion: project.direccion,
            ciudad: project.ciudad,
            matriculaMatriz: project.matriculaMatriz,
          }
        : EMPTY,
    );
  }, [open, project, reset]);

  const onSubmit = handleSubmit(async (values) => {
    setServerError(null);
    try {
      if (project) {
        // ADR-015: el código solo viaja si cambió (el API rechaza cambiarlo tras emitir documentos).
        const { codigo, ...resto } = values;
        const data = codigo !== project.codigo ? { ...resto, codigo } : resto;
        await updateProject.mutateAsync({ id: project.id, data });
      } else {
        await createProject.mutateAsync(values);
      }
      onOpenChange(false);
    } catch (error) {
      setServerError(
        error instanceof Error ? error.message : 'No fue posible guardar el proyecto.',
      );
    }
  });

  const title = isView ? 'Ver proyecto' : isEdit ? 'Editar proyecto' : 'Nuevo proyecto';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            {isView
              ? 'Información del proyecto (solo lectura).'
              : 'Los campos marcados con * son obligatorios.'}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={onSubmit} noValidate className="space-y-4">
          <FormField
            label="Código"
            htmlFor="project-codigo"
            required
            description={AYUDA_CODIGO}
            error={errors.codigo?.message}
          >
            <Input
              id="project-codigo"
              maxLength={3}
              autoCapitalize="characters"
              autoComplete="off"
              className="w-28 uppercase tracking-[0.22em]"
              error={Boolean(errors.codigo)}
              disabled={isView}
              {...registroEnMayusculas(register('codigo'))}
            />
          </FormField>
          <FormField label="Nombre" htmlFor="project-nombre" required error={errors.nombre?.message}>
            <Input
              id="project-nombre"
              error={Boolean(errors.nombre)}
              disabled={isView}
              {...register('nombre')}
            />
          </FormField>
          <FormField
            label="Dirección"
            htmlFor="project-direccion"
            required
            error={errors.direccion?.message}
          >
            <Input
              id="project-direccion"
              error={Boolean(errors.direccion)}
              disabled={isView}
              {...register('direccion')}
            />
          </FormField>
          <FormField label="Ciudad" htmlFor="project-ciudad" required error={errors.ciudad?.message}>
            <Input
              id="project-ciudad"
              error={Boolean(errors.ciudad)}
              disabled={isView}
              {...register('ciudad')}
            />
          </FormField>
          <FormField
            label="Matrícula matriz"
            htmlFor="project-matricula"
            required
            error={errors.matriculaMatriz?.message}
          >
            <Input
              id="project-matricula"
              error={Boolean(errors.matriculaMatriz)}
              disabled={isView}
              {...register('matriculaMatriz')}
            />
          </FormField>
          {serverError && (
            <p role="alert" className="text-sm text-destructive">
              {serverError}
            </p>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {isView ? 'Cerrar' : 'Cancelar'}
            </Button>
            {!isView && (
              <Button type="submit" loading={isSubmitting}>
                Guardar
              </Button>
            )}
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
